import { useState } from 'react'
import { CreditCard, RefreshCw, Check, X, AlertCircle, Clock, Undo2 } from 'lucide-react'
import type { Payment } from '../../hooks/use-payments'

interface PaymentTableProps {
  payments: Payment[]
  loading?: boolean
  onRefund?: (payment: Payment) => void
}

export function PaymentTable({ payments, loading = false, onRefund }: PaymentTableProps) {
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null)

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'succeeded':
        return <Check className="w-4 h-4 text-green-500" />
      case 'pending':
      case 'processing':
        return <Clock className="w-4 h-4 text-amber-500" />
      case 'failed':
        return <X className="w-4 h-4 text-red-500" />
      case 'refunded':
      case 'partially_refunded':
        return <Undo2 className="w-4 h-4 text-blue-500" />
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />
    }
  }

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { label: string; className: string }> = {
      pending: { label: 'Pendente', className: 'bg-amber-100 text-amber-700' },
      processing: { label: 'Processando', className: 'bg-blue-100 text-blue-700' },
      succeeded: { label: 'Pago', className: 'bg-green-100 text-green-700' },
      failed: { label: 'Falhou', className: 'bg-red-100 text-red-700' },
      refunded: { label: 'Reembolsado', className: 'bg-purple-100 text-purple-700' },
      partially_refunded: { label: 'Parcial', className: 'bg-purple-100 text-purple-700' },
      disputed: { label: 'Disputado', className: 'bg-orange-100 text-orange-700' },
    }
    const badge = badges[status] ?? badges.pending!
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badge.className}`}>
        {badge.label}
      </span>
    )
  }

  const formatCurrency = (amount: number, currency = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
    }).format(amount / 100)
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-100" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 border-t border-gray-100 flex items-center px-6">
              <div className="h-4 bg-gray-200 rounded w-1/4" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (payments.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CreditCard className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-500 font-medium">Nenhum pagamento encontrado</p>
        <p className="text-sm text-gray-400 mt-1">Os pagamentos aparecerão aqui quando processados</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pagamento
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Organização
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Valor
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Data
              </th>
              {onRefund && (
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {payments.map((payment) => (
              <tr
                key={payment.id}
                className="hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => setSelectedPayment(payment)}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                      {getStatusIcon(payment.status)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                        {payment.description || `Pagamento #${payment.id.slice(0, 8)}`}
                      </p>
                      {payment.payment_method && (
                        <p className="text-xs text-gray-500">{payment.payment_method}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <p className="text-sm text-gray-900">{payment.organization_name || '-'}</p>
                </td>
                <td className="px-6 py-4">
                  <p className="text-sm font-medium text-gray-900">
                    {formatCurrency(payment.amount, payment.currency)}
                  </p>
                  {payment.refunded_amount > 0 && (
                    <p className="text-xs text-purple-600">
                      -{formatCurrency(payment.refunded_amount, payment.currency)}
                    </p>
                  )}
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(payment.status)}
                </td>
                <td className="px-6 py-4">
                  <p className="text-sm text-gray-500">{formatDate(payment.created_at)}</p>
                </td>
                {onRefund && (
                  <td className="px-6 py-4 text-right">
                    {payment.status === 'succeeded' && payment.refunded_amount === 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onRefund(payment)
                        }}
                        className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="Reembolsar"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payment detail modal */}
      {selectedPayment && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setSelectedPayment(null)}
        >
          <div
            className="bg-white rounded-xl w-full max-w-md m-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Detalhes do Pagamento</h3>
              <button
                onClick={() => setSelectedPayment(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-500 uppercase">ID</p>
                <p className="text-sm font-mono">{selectedPayment.id}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Organização</p>
                <p className="text-sm">{selectedPayment.organization_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Valor</p>
                <p className="text-lg font-semibold">
                  {formatCurrency(selectedPayment.amount, selectedPayment.currency)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Status</p>
                <div className="mt-1">{getStatusBadge(selectedPayment.status)}</div>
              </div>
              {selectedPayment.stripe_payment_intent_id && (
                <div>
                  <p className="text-xs text-gray-500 uppercase">Stripe ID</p>
                  <p className="text-sm font-mono">{selectedPayment.stripe_payment_intent_id}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-gray-500 uppercase">Criado em</p>
                <p className="text-sm">{formatDate(selectedPayment.created_at)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
